import React, { useEffect, useState, useMemo } from 'react'
import Drawer from 'react-modern-drawer'
import 'react-modern-drawer/dist/index.css'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coy } from 'react-syntax-highlighter/dist/esm/styles/prism';
import DrawerHeader from 'app/components/Drawer/DrawerHeader'
import { Button} from 'react-bootstrap'
import { useAuth } from 'app/modules/auth';
import db from 'services/firebase';
import {doc, getDoc} from 'firebase/firestore'

interface CookieConfig {
  companyName: string;
  cookieDuration: number;
  modalTitle: string;
  modalDescription: string;
  theme: {
    maxWidth: string;
    modalMaxWidth: string;
  };
  cookieWindow: {
    modalPreferenceCenterText: string;
    disclaimerText: string;
    manageConsentPreferencesText: string;
    confirmMyChoicesText: string;
    closeModalText: string;
    cookiesTypeStrictlyNecessary: string;
    cookiesTypeStrictlyNecessaryText: string;
    cookiesTypePerformance: string;
    cookiesTypePerformanceText: string;
    cookiesTypeTargeting: string;
    cookiesTypeTargetingText: string;
    cookiesTypeFunctional: string;
    cookiesTypeFunctionalText: string;
  };
  cookieWidget: {
    placement: string;
    allowAllText: string;
    manageSettingsText: string;
    removeAllText: string;
  };
}
export interface addAsset {
  msg: string
  message: string
  checked?: boolean | undefined
}
interface AddAssetsProps {
  isOpen: boolean
  onClose: () => void
}

const CookieGuideDrawer: React.FC<AddAssetsProps> = ({isOpen, onClose}) => {
  const [copied1, setCopied1] = useState(false);
  const [copied2, setCopied2] = useState(false);
  const {currentUser} = useAuth();
  const [cookieConfig, setCookieConfig] = useState<CookieConfig | null>(null);

  const defaultConfig = useMemo(() => ({
    companyName: "secureslate",
    cookieDuration: 365,
    modalTitle: "We Value Your Privacy 🍪",
    modalDescription: "We use cookies to improve your experience, and show personalized content. By clicking on &#39Allow All&#39 you consent to our use of cookies.",
    theme: {
      maxWidth: "350px",
      modalMaxWidth: "600px",
    },
    cookieWindow: {
      modalPreferenceCenterText: "Cookies on {companyName}",
      disclaimerText: "We use cookie to store information about how you use our website, such as the pages you visit. These are the types of cookies:",
      manageConsentPreferencesText: "Choose Which Cookies To Enable",
      confirmMyChoicesText: "Save Changes",
      closeModalText: "Cancel",
      cookiesTypeStrictlyNecessary: "Necessary",
      cookiesTypeStrictlyNecessaryText: "These cookies are essential for website functions like logging in or setting privacy preferences. You can block them, but some features may not work. They don't store personal data.",
      cookiesTypePerformance: "Performance",
      cookiesTypePerformanceText: "These cookies help track site visits and performance anonymously, allowing us to improve the site. Without them, we cannot monitor site activity or performance.",
      cookiesTypeTargeting: "Targeting",
      cookiesTypeTargetingText: "Set by advertising partners, these cookies create a profile of your interests for targeted ads. They don't store personal data, but use your device ID. Blocking them results in less targeted advertising.",
      cookiesTypeFunctional: "Functional",
      cookiesTypeFunctionalText: "These cookies enhance site features and personalization. Some services may not work properly without them, and they can be set by us or third parties."
    },
    cookieWidget: {
      placement: "bl",
      allowAllText: "Allow All",
      manageSettingsText: "Customize",
      removeAllText: "Deny All"
    }
  }), []);

  useEffect(() => {
    if (currentUser && isOpen) {
      const fetchCookieConfig = async () => {
        const tenantRef = doc(db, 'tenants', currentUser.tenantId!);
        const cookieRef = doc(tenantRef, 'settings', 'cookie');
        const cookieDoc = await getDoc(cookieRef);
        
        if (cookieDoc.exists()) {
          const data = cookieDoc.data();
          setCookieConfig(data.cookieWidgetConfig || defaultConfig);
        } else {
          setCookieConfig(defaultConfig);
        }
      };
      
      fetchCookieConfig();
    }
  }, [currentUser, isOpen, defaultConfig]);

  const codeString = cookieConfig ? `<script type="text/javascript" src="https://app.getsecureslate.com/cookie-widget.js" data-cookie-consent='${JSON.stringify(cookieConfig, null, 2).replace(/'/g, '&#39;')}'>
</script>` : '';

 const codeString2=`<script>
function loadScripts() {
  if (window.userConsent) {
    if (window.userConsent.performance) {
      console.log('Performance cookies enabled - loading performance scripts');
      // Example: Analytics scripts
      // const analyticsScript = document.createElement('script');
      // analyticsScript.src = 'your-analytics-url';
      // document.head.appendChild(analyticsScript);
    }

    if (window.userConsent.targeting) {
      console.log('Targeting cookies enabled - loading targeting scripts');
      // Example: Marketing scripts
      // const marketingScript = document.createElement('script');
      // marketingScript.src = 'your-marketing-url';
      // document.head.appendChild(marketingScript);
    }

    if (window.userConsent.functional) {
      console.log('Functional cookies enabled - loading functional scripts');
      // Example: Chat widgets, customization scripts
      // const functionalScript = document.createElement('script');
      // functionalScript.src = 'your-functional-url';
      // document.head.appendChild(functionalScript);
    }
  }
}

if (window.userConsent) {
  loadScripts();
} else {
  document.addEventListener('DOMContentLoaded', function() {
    const cookieConsent = window.cookieConsent;
    if (cookieConsent) {
      const observer = new MutationObserver(function(mutations) {
        if (window.userConsent) {
          loadScripts();
          observer.disconnect();
        }
      });

      observer.observe(document.body, { attributes: true, childList: true, subtree: true });
    }
  });
}
</script>
 `

 const handleCopy1 = () => {
  if (!cookieConfig) return;
  
  const scriptString = `<script type="text/javascript" src="https://app.getsecureslate.com/cookie-widget.js" data-cookie-consent='${JSON.stringify(cookieConfig, null, 2)}'>
</script>`;

  navigator.clipboard.writeText(scriptString).then(() => {
    setCopied1(true);
    setTimeout(() => setCopied1(false), 2000);
  });
};

  const handleCopy2 = () => {
    navigator.clipboard.writeText(codeString2).then(() => {
      setCopied2(true);
      setTimeout(() => setCopied2(false), 2000);
    });
  };

  return (
    <>
      <Drawer open={isOpen} onClose={onClose} direction='right' className='' size='45%'>
        <DrawerHeader title='Installation Guide' onClose={onClose} />
        
        <div className='scroll-y overflow-auto mb-4' tabIndex={-1} style={{height: 'calc(100vh - 50px)', padding:'20px'}}>
          <div className='fw-bold text-gray-900 fs-4' style={{marginBottom:"20px"}}>
            Step 1: Insert the following code into the <code><span>&lt;HEAD&gt;</span>
            </code> section of every  page on your website:
          </div>
          <div className="position-relative ">
            <div className="bg-white rounded border border-1" style={{borderRadius:'8px'}}>
              <Button
                variant="light"
                size="sm"
                className="position-absolute end-0 top-0 m-4 d-flex align-items-center border border-1"
                onClick={handleCopy1}
                disabled={copied1}
                style={{ zIndex: 10, background: "white", borderRadius: "4px", padding: "4px 8px" }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-copy">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z" />
                  <path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" />
                </svg>
              </Button>
              <SyntaxHighlighter
                language='html'
                style={coy}
                showLineNumbers={true}
                customStyle={{
                  background: "transparent",
                  padding: "1.5rem",
                  borderRadius: "8px",
                  fontSize: "0.9rem",
                  lineHeight: 1.5,
                }}
              >
                {codeString}
              </SyntaxHighlighter>
            </div>
          </div>

          <div className='separator' style={{marginBottom:'24px', marginTop:'24px'}}></div>

          <div className='fw-bold text-gray-900 fs-4' style={{marginBottom:"20px"}}>
            Step 2: Ensure that scripts load only after a visiot consents to cookies. For each non-essential <code><span>&lt;SCRIPT&gt;</span>
            </code> tag on your website, update the following:
          </div>
          <div className="position-relative ">
            <div className="bg-white rounded border border-1" style={{borderRadius:'8px'}}>
              <Button
                variant="light"
                size="sm"
                className="position-absolute end-0 top-0 m-4 d-flex align-items-center border border-1"
                onClick={handleCopy2}
                disabled={copied2}
                style={{ zIndex: 10, background: "white", borderRadius: "4px", padding: "4px 8px" }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-copy">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z" />
                  <path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" />
                </svg>
              </Button>
              <SyntaxHighlighter
                language='html'
                style={coy}
                showLineNumbers={true}
                customStyle={{
                  background: "transparent",
                  padding: "1.5rem",
                  borderRadius: "8px",
                  fontSize: "0.9rem",
                  lineHeight: 1.5,
                }}
              >
                {codeString2}
              </SyntaxHighlighter>
            </div>
          </div>
        </div>
      </Drawer>
    </>
  )
}

export default CookieGuideDrawer
